import api from 'services/api'
import useActions from 'hooks/useActions'
import {
  setTokens,
  deleteTokens,
  storedRefreshToken,
  requestAccessToken,
} from 'services/auth'

export const types = {
  RESTORE_DONE: 'auth/RESTORE_DONE',
  GET_USER_SUCCESS: 'auth/GET_USER_SUCCESS',
  GET_USER_FAILURE: 'auth/GET_USER_FAILURE',
  LOGOUT: 'auth/LOGOUT',
}

// The phone number on an ID token's claims, for the not-staff message.
const phoneNumberOf = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(payload)).phone_number
  } catch {
    return null
  }
}

export const getUser = ({ token, refreshToken, authUser }) => {
  setTokens(token, refreshToken)

  return async (dispatch) => {
    try {
      const user = await api.user.getUser(authUser.phoneNumber)

      dispatch({
        type: types.GET_USER_SUCCESS,
        data: {
          token,
          authUser,
          user,
        },
      })
    } catch (error) {
      // Only the API refusing the account signs out. A read that failed
      // (a 5xx, offline) keeps the sign-in, for the iframe's continue to retry.
      const refused = error.status === 401 || error.status === 403
      if (refused) deleteTokens()

      dispatch({
        type: types.GET_USER_FAILURE,
        data: { message: error.message, signOut: refused },
      })
    }
  }
}

// Signs back in with the refresh token kept on this device, without the
// sign-in iframe. With none kept, or one the API turns down, the iframe takes
// over.
export const restoreSession = () => {
  return async (dispatch, getState) => {
    const refreshToken = storedRefreshToken()

    if (refreshToken && !getState().auth.authUser) {
      try {
        const token = await requestAccessToken(refreshToken)
        await dispatch(
          getUser({
            token,
            refreshToken,
            authUser: { phoneNumber: phoneNumberOf(token) },
          })
        )
      } catch (error) {
        // Any answer forgets the token: a revoked one comes back a 500, not a
        // 401. One that never reached the API (offline) is kept for next time.
        if (error.response) deleteTokens()
      }
    }

    dispatch({ type: types.RESTORE_DONE })
  }
}

export const logout = () => {
  deleteTokens()
  return { type: types.LOGOUT }
}

export default useActions.bind(null, {
  restoreSession,
  getUser,
  logout,
})

const initialState = {
  token: null,
  authUser: null,
  user: null,
  error: null,
  loggedOut: false,
  restoring: true,
}

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case types.RESTORE_DONE:
      return {
        ...state,
        restoring: false,
      }

    case types.GET_USER_SUCCESS:
      return {
        ...state,
        token: action.data.token,
        authUser: action.data.authUser,
        user: action.data.user,
        error: null,
        loggedOut: false,
      }

    case types.GET_USER_FAILURE:
      return {
        ...state,
        authUser: null,
        user: null,
        error: action.data.message,
        loggedOut: action.data.signOut,
      }

    case types.LOGOUT:
      return {
        token: null,
        authUser: null,
        user: null,
        error: null,
        loggedOut: true,
        restoring: false,
      }

    default:
      return state
  }
}
